import { useState, useEffect, useCallback } from 'react';
import { useReaderAuth } from '../context/ReaderAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook to manage module completion and certificates
 */
export const useModuleCompletion = () => {
  const { token, isAuthenticated } = useReaderAuth();
  const [completions, setCompletions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Safe check for authentication
  const checkAuth = useCallback(() => {
    try {
      return isAuthenticated && typeof isAuthenticated === 'function' ? isAuthenticated() : false;
    } catch {
      return false;
    }
  }, [isAuthenticated]);

  // Fetch all user completions
  const fetchCompletions = useCallback(async () => {
    if (!checkAuth() || !token) {
      setCompletions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/modules/completions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompletions(data.completions || []);
      } else {
        setCompletions([]);
      }
    } catch (err) {
      console.error('Error fetching completions:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token, checkAuth]);

  useEffect(() => {
    fetchCompletions();
  }, [fetchCompletions]);

  // Mark a module as complete
  const markComplete = async (moduleNumber) => {
    if (!checkAuth() || !token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_URL}/api/modules/${moduleNumber}/complete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to mark module complete');
    }

    const data = await response.json();
    
    // Refresh completions
    await fetchCompletions();
    
    return data;
  };

  // Check if a specific module is completed
  const isModuleCompleted = (moduleNumber) => {
    return completions.some(c => c.module_number === moduleNumber);
  };

  // Get completion data for a specific module
  const getModuleCompletion = (moduleNumber) => {
    return completions.find(c => c.module_number === moduleNumber);
  };

  // Generate certificate download URL
  const getCertificateUrl = (moduleNumber, format = 'pdf') => {
    return `${API_URL}/api/modules/${moduleNumber}/certificate/${format}`;
  };

  // Download certificate (handles auth header)
  const downloadCertificate = async (moduleNumber, format = 'pdf') => {
    if (!checkAuth() || !token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_URL}/api/modules/${moduleNumber}/certificate/${format}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to download certificate');
    }

    // Get the blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Get filename from Content-Disposition header or generate one
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `Certificate_Module${moduleNumber}.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename=(.+)/);
      if (match) {
        filename = match[1];
      }
    }
    
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return {
    completions,
    isLoading,
    error,
    markComplete,
    isModuleCompleted,
    getModuleCompletion,
    getCertificateUrl,
    downloadCertificate,
    refetch: fetchCompletions
  };
};

export default useModuleCompletion;
